// Shipping row, address line, delivery estimate, free-shipping bar and
// cashback block — shared by the cart drawer and the cart page.
//
// Picks a CEP from (in priority order) localStorage, then customer.default_address
// rendered into a data attribute, then prompts the user inline. Fetches the
// cheapest paid rate via Shopify's prepare → poll shipping_rates flow.
//
// Free shipping (Octolize Shipping Discounts app) is mirrored client-side: when
// the CEP resolves to an in-zone state/city AND the Pix-discounted cart minus any
// active cashback simulation meets the threshold, the cheapest rate is shown as
// "Grátis" and the bar congratulates the customer.
//
// Both surfaces are mounted at once on /cart, so this is a factory rather than a
// singleton: one controller per root, each owning its own edit/simulation state
// and its own MutationObserver (common.js#getAndUpdateMinicartInFront swaps
// .cart-drawer.innerHTML and, on the cart page, #cart-template.innerHTML). The
// CEP, address, zone and rate lookups are shared across controllers — same
// customer, same cart, so a second round trip would only invite the two to
// disagree.

(function () {
  'use strict';

  if (window.__vinselCartDrawerShipping) return;
  window.__vinselCartDrawerShipping = true;

  // Each controller finds its own markup under one of these containers. The
  // totals element carries data-cart-totals in both.
  var TOTALS = '[data-cart-totals]';
  var SCOPES = ['.cart-drawer', '#cart-template'];

  var STORAGE_KEY = 'vinsel:cep';
  var COUNTRY = 'Brazil';

  // Free-shipping zone rules. Mirrors product-shipping-calculator.js#FREE_SHIPPING_RULES
  // and the Octolize app config — keep these three places in sync. The minimum-cart
  // threshold is read per-render from [data-shipping-min-cents] on the totals element.
  var FREE_SHIPPING_STATES = ['São Paulo'];
  var FREE_SHIPPING_CITIES = ['Rio de Janeiro', 'Seropédica', 'Niterói', 'São Gonçalo', 'Itaboraí', 'Tanguá', 'Maricá', 'Duque de Caxias', 'São João de Meriti', 'Petrópolis', 'Magé', 'Guapimirim', 'Nova Iguaçu', 'Belford Roxo', 'Queimados', 'Japeri', 'Nilópolis', 'Mesquita', 'Vitória', 'Vila Velha', 'Viana', 'Cariacica', 'Serra', 'Fundão', 'Guarapari', 'Belo Horizonte', 'Contagem', 'Ibirité', 'Sarzedo', 'Mário Campos', 'Brumadinho', 'Betim', 'Igarapé', 'São Joaquim de Bicas', 'Santa Luzia', 'Vespasiano', 'Lagoa Santa', 'Pedro Leopoldo', 'São José da Lapa', 'Confins', 'Ribeirão das Neves', 'Taquaraçu de Minas', 'Nova Lima', 'Rio Acima', 'Raposos', 'Sabará', 'Caeté', 'Nova União', 'Itabirito', 'Mateus Leme', 'Juatuba', 'Florestal', 'Sete Lagoas', 'Matozinhos', 'Capim Branco', 'Funilândia', 'Prudente de Morais', 'Jaboticatubas', 'Barão de Cocais', 'Curitiba', 'São José dos Pinhais', 'Piraquara', 'Pinhais', 'Colombo', 'Quatro Barras', 'Campina Grande do Sul', 'Bocaiúva do Sul', 'Almirante Tamandaré', 'Campo Magro', 'Rio Branco do Sul', 'Itaperuçu', 'Campo Largo', 'Balsa Nova', 'Araucária', 'Contenda', 'Mandirituba', 'Fazenda Rio Grande', 'Florianópolis', 'São José', 'São Pedro de Alcântara', 'Palhoça', 'Santo Amaro da Imperatriz', 'Águas Mornas', 'Biguaçu', 'Antônio Carlos', 'Governador Celso Ramos', 'Tijucas', 'Canelinha', 'São João Batista', 'Major Gercino', 'Nova Trento', 'Angelina', 'Rancho Queimado', 'São Bonifácio', 'Paulo Lopes', 'Garopaba', 'Porto Alegre', 'Canoas', 'Nova Santa Rita', 'Montenegro', 'Guaíba', 'Eldorado do Sul', 'São Leopoldo', 'Portão', 'Sapucaia do Sul', 'Esteio', 'Novo Hamburgo', 'Estância Velha', 'Campo Bom', 'Sapiranga', 'Ivoti', 'Dois Irmãos', 'Gravataí', 'Glorinha', 'Viamão', 'Tramandaí', 'Osório', 'Capão da Canoa', 'Alvorada', 'Cachoeirinha', 'Capela de Santana', 'Triunfo', 'São Jerônimo', 'Arroio dos Ratos', 'Charqueadas'];

  // CEP prefix → Brazilian state. Mirrors product-shipping-calculator.js.
  // Looked up longest-prefix-first (3 → 2 → 1).
  var ZIP_PREFIX_TO_STATE = {
    '689': 'Amapá', '699': 'Acre', '693': 'Roraima',
    '0': 'São Paulo', '1': 'São Paulo',
    '20': 'Rio de Janeiro', '21': 'Rio de Janeiro', '22': 'Rio de Janeiro',
    '23': 'Rio de Janeiro', '24': 'Rio de Janeiro', '25': 'Rio de Janeiro',
    '26': 'Rio de Janeiro', '27': 'Rio de Janeiro', '28': 'Rio de Janeiro',
    '29': 'Espírito Santo',
    '3': 'Minas Gerais',
    '40': 'Bahia', '41': 'Bahia', '42': 'Bahia', '43': 'Bahia', '44': 'Bahia',
    '45': 'Bahia', '46': 'Bahia', '47': 'Bahia', '48': 'Bahia',
    '49': 'Sergipe',
    '50': 'Pernambuco', '51': 'Pernambuco', '52': 'Pernambuco',
    '53': 'Pernambuco', '54': 'Pernambuco', '55': 'Pernambuco', '56': 'Pernambuco',
    '57': 'Alagoas',
    '58': 'Paraíba',
    '59': 'Rio Grande do Norte',
    '60': 'Ceará', '61': 'Ceará', '62': 'Ceará', '63': 'Ceará',
    '64': 'Piauí',
    '65': 'Maranhão',
    '66': 'Pará', '67': 'Pará', '68': 'Pará',
    '69': 'Amazonas',
    '70': 'Distrito Federal', '71': 'Distrito Federal',
    '72': 'Distrito Federal', '73': 'Distrito Federal',
    '74': 'Goiás', '75': 'Goiás', '76': 'Goiás',
    '77': 'Tocantins',
    '78': 'Mato Grosso',
    '79': 'Mato Grosso do Sul',
    '80': 'Paraná', '81': 'Paraná', '82': 'Paraná', '83': 'Paraná',
    '84': 'Paraná', '85': 'Paraná', '86': 'Paraná', '87': 'Paraná',
    '88': 'Santa Catarina', '89': 'Santa Catarina',
    '9': 'Rio Grande do Sul'
  };

  function digitsOnly(s) { return (s || '').replace(/\D/g, ''); }

  function isValidCep(cep) {
    return digitsOnly(cep).length === 8;
  }

  function formatCep(cep) {
    var d = digitsOnly(cep);
    if (d.length !== 8) return d;
    return d.slice(0, 5) + '-' + d.slice(5);
  }

  function provinceFromCep(cep) {
    var d = digitsOnly(cep);
    if (d.length < 2) return null;
    return (
      ZIP_PREFIX_TO_STATE[d.slice(0, 3)] ||
      ZIP_PREFIX_TO_STATE[d.slice(0, 2)] ||
      ZIP_PREFIX_TO_STATE[d.slice(0, 1)] ||
      null
    );
  }

  function readCep() {
    try {
      var stored = digitsOnly(localStorage.getItem(STORAGE_KEY) || '');
      if (stored.length === 8) return stored;
    } catch (_) { /* private mode / disabled storage */ }
    var root = document.querySelector('[data-customer-zip]');
    if (!root) return '';
    var fromCustomer = digitsOnly(root.getAttribute('data-customer-zip'));
    if (fromCustomer.length === 8) return fromCustomer;
    return '';
  }

  function persistCep(cep) {
    try { localStorage.setItem(STORAGE_KEY, digitsOnly(cep)); } catch (_) {}
  }

  function brl(cents) {
    return 'R$ ' + (cents / 100)
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // Substitutes {amount} in a section-provided copy template. The template is
  // authored in the theme, not user input; the amount is our own formatted
  // currency string, so neither needs escaping here.
  function fillTemplate(template, fallback, amount) {
    return String(template || fallback).replace('{amount}', amount);
  }

  function priceToCents(priceStr) {
    var n = parseFloat(priceStr);
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Every write into the drawer trips the MutationObserver that re-runs init(),
  // so re-writing identical markup spins forever — that loop is what made the
  // control flicker between "calculando…" and "Indisponível" on a CEP with no
  // rates. Only touch the DOM when the markup actually changes.
  function setHtml(el, html) {
    if (!el || el.innerHTML === html) return;
    el.innerHTML = html;
  }

  // Pin icon for the resolved-address line. Mirrors product-shipping-calculator.js.
  var PIN_ICON =
    '<svg width="12" height="14" viewBox="0 0 12 14" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M6 .8a4.7 4.7 0 0 0-4.7 4.7c0 3.4 4.2 7.5 4.4 7.7a.4.4 0 0 0 .6 0c.2-.2 4.4-4.3 4.4-7.7A4.7 4.7 0 0 0 6 .8Zm0 6.5a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z"></path>' +
    '</svg>';

  // Full address for a CEP. Mirrors product-shipping-calculator.js#getAddressFromCep
  // — keep behavior in sync. Three outcomes callers must tell apart: an address
  // object, null when the providers agree the CEP does not exist, and undefined
  // when the lookup itself failed and nothing can be concluded.
  function getAddressFromCep(cep) {
    var cacheKey = 'cepAddr:' + cep;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (_) {}

    function fetchWithTimeout(url, ms) {
      var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, ms);
      return fetch(url, ctrl ? { signal: ctrl.signal } : {})
        .then(function (r) {
          if (r && r.status === 404) return { missing: true };
          return r && r.ok ? r.json() : null;
        })
        .catch(function () { return null; })
        .then(function (v) { clearTimeout(timer); return v; });
    }

    var viaCep;
    return fetchWithTimeout('https://viacep.com.br/ws/' + cep + '/json/', 2500)
      .then(function (a) {
        viaCep = a;
        if (a && !a.erro && !a.missing && a.localidade) {
          return { street: a.logradouro || '', city: a.localidade, uf: a.uf || '' };
        }
        return fetchWithTimeout('https://brasilapi.com.br/api/cep/v1/' + cep, 2500)
          .then(function (b) {
            if (b && b.city) {
              return { street: b.street || '', city: b.city, uf: b.state || '' };
            }
            // At least one provider answered, and the answer was "no such CEP".
            if ((viaCep && (viaCep.erro || viaCep.missing)) || (b && b.missing)) return null;
            return undefined;
          });
      })
      .then(function (address) {
        if (address !== undefined) {
          try { sessionStorage.setItem(cacheKey, JSON.stringify(address)); } catch (_) {}
        }
        return address;
      });
  }

  // "Rua das Flores, São Paulo - SP", dropping whichever parts the CEP doesn't
  // carry — CEPs that cover a whole município have no logradouro.
  function formatCepAddress(address) {
    if (!address) return '';
    var parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.uf ? address.city + ' - ' + address.uf : address.city);
    else if (address.uf) parts.push(address.uf);
    return parts.join(', ');
  }

  // Zone status per CEP, backed by the same address lookup that feeds the
  // address line, so a CEP costs one round trip and not two. SP-state CEPs
  // resolve synchronously; everything else waits on the lookup. While pending
  // we render optimistically as in-zone — most non-SP customers are in the
  // capitais list, and the brief re-render if they aren't is preferable to
  // either flicker or hiding the bar for everyone outside SP.
  var zoneCache = {};
  var addressCache = {};   // cep -> address object, or null when not found
  var addressWaiters = {}; // cep -> [callback]
  var addressFailed = {};  // cep -> true; lookup failed, don't hammer the providers

  function getZoneSync(cep) {
    if (!cep) return null;
    if (zoneCache[cep] !== undefined) return zoneCache[cep];
    var province = provinceFromCep(cep);
    if (FREE_SHIPPING_STATES.indexOf(province) >= 0) {
      zoneCache[cep] = true;
      return true;
    }
    return null;
  }

  function getAddressSync(cep) {
    if (!cep) return undefined;
    return Object.prototype.hasOwnProperty.call(addressCache, cep)
      ? addressCache[cep]
      : undefined;
  }

  function ensureAddressFetched(cep, onResolve) {
    if (!cep) return;
    if (Object.prototype.hasOwnProperty.call(addressCache, cep)) {
      if (onResolve) onResolve(addressCache[cep]);
      return;
    }
    if (addressFailed[cep]) {
      if (onResolve) onResolve(undefined);
      return;
    }
    if (addressWaiters[cep]) {
      if (onResolve) addressWaiters[cep].push(onResolve);
      return;
    }
    addressWaiters[cep] = onResolve ? [onResolve] : [];

    getAddressFromCep(cep).then(function (address) {
      var waiters = addressWaiters[cep] || [];
      delete addressWaiters[cep];
      if (address === undefined) {
        addressFailed[cep] = true;
      } else {
        addressCache[cep] = address;
        // Don't clobber a zone already decided by state — São Paulo city is
        // not in the capitais list, so a city check alone would flip an
        // SP-state CEP to out-of-zone.
        if (zoneCache[cep] === undefined) {
          zoneCache[cep] =
            FREE_SHIPPING_STATES.indexOf(provinceFromCep(cep)) >= 0 ||
            !!(address && FREE_SHIPPING_CITIES.indexOf(address.city) >= 0);
        }
      }
      waiters.forEach(function (fn) { fn(address); });
    });
  }

  function ensureZoneFetched(cep, onResolve) {
    if (!cep) return;
    if (zoneCache[cep] !== undefined) {
      if (onResolve) onResolve(zoneCache[cep]);
      return;
    }
    // Only call back once the zone is actually known. Calling back on a failed
    // lookup would re-enter renderShippingBar, which called us, forever.
    ensureAddressFetched(cep, function () {
      if (onResolve && zoneCache[cep] !== undefined) onResolve(zoneCache[cep]);
    });
  }

  // Mirrors product-shipping-calculator.js#formatEta / #estimateBusinessDays /
  // #daysFromText — keep the two in sync. Structured fields first, then the
  // rate's prose, then a plain label rather than an empty cell.
  var ETA_UNKNOWN_TEXT = 'Prazo a confirmar';

  function formatEta(rate) {
    var days = estimateBusinessDays(rate);
    if (days === null) return ETA_UNKNOWN_TEXT;
    if (days <= 1) return 'Em até 1 dia útil';
    return 'Em até ' + days + ' dias úteis';
  }

  function estimateBusinessDays(rate) {
    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length > 0) {
      var days = rate.delivery_days.map(Number).filter(function (d) {
        return isFinite(d);
      });
      if (days.length > 0) return Math.max.apply(null, days);
    }

    var range = rate.delivery_range;
    var dateStr =
      (Array.isArray(range) && range.length > 0 ? range[range.length - 1] : null) ||
      rate.delivery_date;
    if (dateStr) {
      var target = parseLocalDate(dateStr);
      if (target) {
        var cursor = new Date();
        cursor.setHours(0, 0, 0, 0);
        var businessDays = 0;
        while (cursor < target) {
          cursor.setDate(cursor.getDate() + 1);
          var weekday = cursor.getDay();
          if (weekday !== 0 && weekday !== 6) businessDays++;
        }
        return businessDays;
      }
    }

    // Which field the app uses varies, so try each one that could hold prose.
    var texts = [rate.description, rate.presentment_name, rate.name];
    for (var i = 0; i < texts.length; i++) {
      var fromText = daysFromText(texts[i]);
      if (fromText !== null) return fromText;
    }
    return null;
  }

  // "5 a 8 dias úteis" -> 8, "entrega em 7 dias úteis" -> 7, "até 24 horas" -> 1.
  // Ranges resolve to the upper bound, so the number on screen is the one the
  // customer can hold you to.
  function daysFromText(text) {
    if (!text) return null;
    var s = String(text).toLowerCase();

    var range = /(\d+)\s*(?:a|at[éê]|-|–|—|e)\s*(\d+)\s*dias?/.exec(s);
    if (range) return Math.max(Number(range[1]), Number(range[2]));

    var single = /(\d+)\s*dias?\b/.exec(s);
    if (single) return Number(single[1]);

    var hours = /(\d+)\s*horas?\b/.exec(s);
    if (hours) return Number(hours[1]) <= 24 ? 1 : Math.ceil(Number(hours[1]) / 24);

    return null;
  }

  // "2026-10-05" through the Date constructor is UTC midnight, which is still
  // the previous day in BRT and costs a day off the count. Build it locally.
  function parseLocalDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) {
      var parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  function pickCheapest(rates) {
    if (!Array.isArray(rates) || rates.length === 0) return null;
    var cheapest = null;
    rates.forEach(function (r) {
      var cents = priceToCents(r.price);
      if (cents === null) return;
      if (!cheapest || cents < cheapest.cents) {
        cheapest = { cents: cents, name: r.name, eta: formatEta(r) };
      }
    });
    return cheapest;
  }

  // Shopify computes rates asynchronously: GET shipping_rates.json returns 422
  // while still working, then 200 with the rates once ready. With a carrier
  // service in the mix (Cota Frete calling the transportadoras) it can also
  // answer 200 with an empty list before the carrier has replied, so poll on
  // both — an empty list only means "nothing ships there" once the ladder runs
  // out. Mirrors product-shipping-calculator.js#getShippingRates.
  function pollShippingRates(qs, attempt) {
    attempt = attempt || 0;
    var delays = [200, 350, 500, 750, 1000, 1300, 1600, 1900, 2200, 2500];

    function retry() {
      return new Promise(function (resolve) {
        setTimeout(resolve, delays[attempt]);
      }).then(function () {
        return pollShippingRates(qs, attempt + 1);
      });
    }

    return fetch('/cart/shipping_rates.json' + qs, {
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (r.status === 422) {
        if (attempt >= delays.length) throw new Error('shipping_rates: timeout');
        return retry();
      }
      if (!r.ok) throw new Error('shipping_rates: ' + r.status);
      return r.json().then(function (json) {
        var rates = (json && json.shipping_rates) || [];
        if (rates.length === 0 && attempt < delays.length) return retry();
        return json;
      });
    });
  }

  function fetchRates(cep) {
    var province = provinceFromCep(cep);
    var body = new URLSearchParams();
    body.set('shipping_address[zip]', cep);
    body.set('shipping_address[country]', COUNTRY);
    if (province) body.set('shipping_address[province]', province);

    var qs =
      '?shipping_address%5Bzip%5D=' + encodeURIComponent(cep) +
      '&shipping_address%5Bcountry%5D=' + encodeURIComponent(COUNTRY);
    if (province) qs += '&shipping_address%5Bprovince%5D=' + encodeURIComponent(province);

    return fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    }).then(function () {
      return pollShippingRates(qs, 0);
    });
  }

  // Rates cached by signature (cep|products_total_cents) and shared by every
  // controller, so a re-render shows the rate instantly and identical state
  // doesn't re-fetch.
  var rateCache = {};

  // One lookup per signature no matter how many controllers want it. On /cart
  // the drawer and the page summary are both mounted; two prepare_shipping_rates
  // against the same cart is precisely the race that used to hand one surface
  // the other's numbers.
  var rateInFlight = {};

  function fetchRatesShared(signature, cep) {
    if (rateInFlight[signature]) return rateInFlight[signature];
    var pending = fetchRates(cep);
    rateInFlight[signature] = pending;
    function release() { delete rateInFlight[signature]; }
    pending.then(release, release);
    return pending;
  }

  // ---------------------------------------------------------------------
  // One controller per root.
  // ---------------------------------------------------------------------
  function createController(containerSelector) {
    function container() { return document.querySelector(containerSelector); }

    // Scoped lookup: the drawer and the cart page render the same data
    // attributes, so an unscoped query would cross the two on /cart.
    function q(sel) {
      var root = container();
      return root ? root.querySelector(sel) : null;
    }

    var inFlightSignature = '';
    var editMode = false;
    // True between clicking the CEP and the user's first keystroke. While
    // pristine, the Total keeps the cached shipping so clicking alterar
    // doesn't visually change the price; the moment the user types, we
    // drop shipping from the Total until the next successful calc.
    var editPristine = false;

    // Cashback simulation state. When the user clicks "Simular", we deduct
    // min(balance, current total) from the Total and recompute the earn line.
    var cashbackSimulating = false;
    var cashbackUsedCents = 0;
    // currentShippingCents is the effective shipping after free-shipping eligibility
    // (i.e. 0 when Octolize would discount). rawShippingCents is the cheapest paid
    // rate as returned by the API, kept so we can show the original price crossed
    // out next to "Grátis" and revert to it if the user's cashback simulation
    // drops the cart below the threshold.
    var currentShippingCents = 0;
    var rawShippingCents = 0;
    var hasShippingRate = false;
    // Delivery estimate for the rate currently on screen.
    var currentEtaText = '';
    // CEP the customer is typing while in edit mode, so the address line can
    // confirm the destination before they commit to it.
    var editTypedCep = '';

    function getCashbackBalanceCents() {
      var row = q('[data-cashback-row]');
      if (!row) return 0;
      return parseInt(row.getAttribute('data-cashback-balance-cents'), 10) || 0;
    }

    // A failure is cached so the drawer stops re-fetching (and re-rendering,
    // and re-fetching...), but the customer re-submitting the CEP is an explicit
    // request to try again.
    function clearFailedLookups(cep) {
      Object.keys(rateCache).forEach(function (key) {
        if (key.indexOf(cep + '|') === 0 && rateCache[key] && rateCache[key].failed) {
          delete rateCache[key];
        }
      });
      delete addressFailed[cep];
    }

    function startEdit() {
      editMode = true;
      editPristine = true;
      init();
      var input = q('[data-shipping-input]');
      if (input) { input.focus(); input.select(); }
    }

    function attemptSubmit() {
      var input = q('[data-shipping-input]');
      if (!input) return;
      var cep = digitsOnly(input.value);
      if (!isValidCep(cep)) {
        input.classList.add('is-invalid');
        input.focus();
        return;
      }
      // Eight well-formed digits is not the same as a real CEP. Resolve the
      // address first, so an unknown CEP is reported here instead of turning
      // into an unexplained "Indisponível" on the rate row.
      clearFailedLookups(cep);
      ensureAddressFetched(cep, function (address) {
        if (address === null) {
          var live = q('[data-shipping-input]');
          if (live) { live.classList.add('is-invalid'); live.focus(); }
          renderShippingDetails(null, '', 'CEP não encontrado');
          return;
        }
        persistCep(cep);
        editTypedCep = '';
        editMode = false;
        editPristine = false;
        init();
      });
    }

    function cancelEdit() {
      editMode = false;
      editPristine = false;
      editTypedCep = '';
      init();
    }

    function renderCepBtn(slot, cep) {
      slot.innerHTML = '';
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cart-drawer-shipping-cep-link';
      b.title = 'Alterar CEP';
      b.setAttribute('aria-label', 'Alterar CEP');
      b.textContent = formatCep(cep);
      b.addEventListener('click', startEdit);
      slot.appendChild(b);
    }

    function renderCepInput(slot, presetCep) {
      slot.innerHTML = '';

      var input = document.createElement('input');
      input.type = 'text';
      input.inputMode = 'numeric';
      input.maxLength = 9;
      input.placeholder = '00000-000';
      input.className = 'cart-drawer-shipping-input';
      input.setAttribute('aria-label', 'Informe seu CEP');
      input.setAttribute('data-shipping-input', '');
      if (presetCep) input.value = formatCep(presetCep);

      input.addEventListener('input', function () {
        input.classList.remove('is-invalid');
        var d = digitsOnly(input.value);
        input.value = d.length > 5 ? d.slice(0, 5) + '-' + d.slice(5, 8) : d;

        // Confirm the destination as soon as a full CEP is on screen.
        editTypedCep = d;
        if (isValidCep(d)) {
          ensureAddressFetched(d, function () {
            if (editTypedCep === d) renderDetailsForCurrentCep();
          });
        }
        renderDetailsForCurrentCep();

        // First keystroke after entering edit: the user is changing the CEP,
        // so the previously-cached shipping no longer applies — drop it from
        // the Total until a fresh rate comes back.
        if (editPristine) {
          editPristine = false;
          rawShippingCents = 0;
          hasShippingRate = false;
          renderState();
        }
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); attemptSubmit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
      });

      slot.appendChild(input);
    }

    function renderControlAmount(controlEl) {
      if (currentShippingCents === 0 && rawShippingCents > 0) {
        // Octolize free shipping: "Grátis" tag on the left, original price
        // crossed out on the right so the discount value stays visible.
        setHtml(controlEl,
          '<span class="cart-drawer-shipping-amount-free">Grátis</span>' +
          '<span class="cart-drawer-shipping-amount-original">' +
            escapeHtml(brl(rawShippingCents)) +
          '</span>');
        return;
      }
      setHtml(controlEl,
        '<span class="cart-drawer-shipping-amount">' +
          escapeHtml(currentShippingCents === 0 ? 'Grátis' : brl(currentShippingCents)) +
        '</span>');
    }

    function renderControlLoading(controlEl) {
      setHtml(controlEl, '<span class="cart-drawer-shipping-loading">calculando…</span>');
    }

    function renderControlError(controlEl) {
      setHtml(controlEl, '<span class="cart-drawer-shipping-error">Indisponível</span>');
    }

    function renderControlNotFound(controlEl) {
      setHtml(controlEl, '<span class="cart-drawer-shipping-error">CEP não encontrado</span>');
    }

    // The line under the shipping row: resolved address on the left, delivery
    // estimate on the right. Either half may be empty — CEPs that cover a whole
    // município carry no logradouro, and rates without a carrier estimate carry
    // no prazo.
    function renderShippingDetails(address, etaText, noticeText) {
      var el = q('[data-shipping-details]');
      if (!el) return;

      var left = '';
      if (noticeText) {
        left = PIN_ICON + '<span>' + escapeHtml(noticeText) + '</span>';
      } else {
        var addressText = formatCepAddress(address);
        if (addressText) left = PIN_ICON + '<span>' + escapeHtml(addressText) + '</span>';
      }

      if (!left && !etaText) {
        setHtml(el, '');
        el.hidden = true;
        return;
      }

      setHtml(el,
        '<span class="cart-drawer-shipping-address' + (noticeText ? ' is-notfound' : '') + '">' +
          left +
        '</span>' +
        '<span class="cart-drawer-shipping-eta">' + (etaText ? escapeHtml(etaText) : '') + '</span>');
      el.hidden = false;
    }

    function renderControlCalcButton(controlEl) {
      controlEl.innerHTML = '';
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cart-drawer-shipping-submit';
      b.textContent = 'calcular';
      b.addEventListener('click', attemptSubmit);
      controlEl.appendChild(b);
    }

    // Decides whether free shipping applies right now and sets currentShippingCents
    // accordingly. The bar's eligibility uses cart_pix - cashback_used (with cashback
    // capped at products only — i.e. the credit reduces the product portion). This
    // matches what the customer perceives "really paying" toward the threshold.
    function applyEligibility() {
      var rootTotals = q(TOTALS);
      if (!rootTotals) {
        currentShippingCents = hasShippingRate ? rawShippingCents : 0;
        return;
      }

      var products = parseInt(rootTotals.getAttribute('data-products-total-cents'), 10) || 0;
      var minCents = parseInt(rootTotals.getAttribute('data-shipping-min-cents'), 10) || 0;

      var cep = readCep();
      var inZoneSync = getZoneSync(cep);
      // Optimistic while async pending; conservative when no CEP at all.
      var inZone;
      if (!cep) inZone = false;
      else if (inZoneSync === null) inZone = true;
      else inZone = inZoneSync;

      var balance = getCashbackBalanceCents();
      var simActive = cashbackSimulating && balance > 0;
      var cashbackForBar = simActive ? Math.min(balance, products) : 0;
      var effectiveBase = Math.max(0, products - cashbackForBar);

      var eligible = inZone && hasShippingRate && minCents > 0 && effectiveBase >= minCents;
      currentShippingCents = eligible ? 0 : (hasShippingRate ? rawShippingCents : 0);
    }

    function updateTotal(rootEl) {
      applyCashbackSimulation();

      var totalEl = rootEl.querySelector('[data-total-amount]');
      if (totalEl) {
        var products = parseInt(rootEl.getAttribute('data-products-total-cents'), 10) || 0;
        var sum = Math.max(0, products + currentShippingCents - cashbackUsedCents);
        var newText = brl(sum);
        if (totalEl.textContent !== newText) {
          totalEl.textContent = newText;
          // Restart the flicker animation so it fires on every actual change,
          // not just the first time the class is added.
          totalEl.classList.remove('is-flickering');
          void totalEl.offsetWidth;
          totalEl.classList.add('is-flickering');
        }
      }
      updateCashbackEarn();
    }

    // Recomputes the "Você ganhará X para usar no seu próximo pedido" line.
    // Always runs because
    // the base depends on shipping (when mode='include') and on any active
    // cashback simulation.
    function updateCashbackEarn() {
      var row = q('[data-cashback-row]');
      if (!row) return;

      var earnEl = row.querySelector('[data-cashback-earn]');
      if (!earnEl) return;

      var pct = parseFloat(row.getAttribute('data-cashback-pct'));
      var pixMul = parseFloat(row.getAttribute('data-cashback-pix-multiplier'));
      var subtotalCents = parseInt(row.getAttribute('data-cashback-subtotal-cents'), 10);
      if (isNaN(pct) || isNaN(pixMul) || isNaN(subtotalCents)) return;

      var minCents = parseInt(row.getAttribute('data-cashback-min-cents'), 10) || 0;
      var maxCents = parseInt(row.getAttribute('data-cashback-max-cents'), 10) || 0;
      var mode = row.getAttribute('data-cashback-shipping-mode');

      var baseCents = Math.round(subtotalCents * pixMul);
      if (mode === 'include') baseCents += currentShippingCents;
      baseCents -= cashbackUsedCents;
      if (baseCents < 0) baseCents = 0;

      // The wording lives on the row as a {amount} template, rendered by the
      // section. Keeping it in one place stops the server-rendered sentence and
      // this recomputed one from drifting apart — they had, over a full stop.
      var html;
      if (minCents > 0 && baseCents < minCents) {
        html = fillTemplate(
          row.getAttribute('data-cashback-below-template'),
          'Compre mais <strong>{amount}</strong> para começar a ganhar.',
          brl(minCents - baseCents)
        );
      } else {
        var earn = Math.round(baseCents * pct / 100);
        if (maxCents > 0 && earn > maxCents) earn = maxCents;
        html = fillTemplate(
          row.getAttribute('data-cashback-earn-template'),
          'Você ganhará <strong>{amount}</strong> para usar no seu próximo pedido.',
          brl(earn)
        );
      }
      if (earnEl.innerHTML !== html) earnEl.innerHTML = html;
    }

    // Reflects the current simulation state into the DOM: shows/hides the
    // "Cashback a utilizar" row and toggles the balance line between its
    // default ("disponíveis · Simular") and using ("Usando R$ X") variants.
    // Recomputes cashbackUsedCents whenever the cart or shipping changes
    // while a simulation is active, so the deduction stays in sync.
    function applyCashbackSimulation() {
      var rootTotals = q(TOTALS);
      var useRow = rootTotals ? rootTotals.querySelector('[data-cashback-use-row]') : null;
      var useAmount = rootTotals ? rootTotals.querySelector('[data-cashback-use-amount]') : null;

      var cashbackRow = q('[data-cashback-row]');
      var defaultEl = cashbackRow ? cashbackRow.querySelector('[data-cashback-balance-default]') : null;
      var usingEl = cashbackRow ? cashbackRow.querySelector('[data-cashback-balance-using]') : null;
      var usingAmount = cashbackRow ? cashbackRow.querySelector('[data-cashback-using-amount]') : null;

      var balance = cashbackRow
        ? (parseInt(cashbackRow.getAttribute('data-cashback-balance-cents'), 10) || 0)
        : 0;

      if (!cashbackSimulating || !cashbackRow || balance <= 0 || !rootTotals) {
        cashbackUsedCents = 0;
        if (useRow) useRow.hidden = true;
        if (defaultEl) defaultEl.hidden = false;
        if (usingEl) usingEl.hidden = true;
        return;
      }

      var products = parseInt(rootTotals.getAttribute('data-products-total-cents'), 10) || 0;
      var totalBefore = products + currentShippingCents;
      var used = Math.min(balance, Math.max(0, totalBefore));
      cashbackUsedCents = used;

      if (used <= 0) {
        // Nothing to deduct (cart effectively zero) — bail out cleanly.
        cashbackSimulating = false;
        if (useRow) useRow.hidden = true;
        if (defaultEl) defaultEl.hidden = false;
        if (usingEl) usingEl.hidden = true;
        return;
      }

      if (useRow) useRow.hidden = false;
      if (useAmount) useAmount.textContent = '− ' + brl(used);
      if (defaultEl) defaultEl.hidden = true;
      if (usingEl) usingEl.hidden = false;
      if (usingAmount) usingAmount.textContent = brl(used);
    }

    function startCashbackSimulation(e) {
      if (e) e.preventDefault();
      cashbackSimulating = true;
      renderState();
    }

    function cancelCashbackSimulation(e) {
      if (e) e.preventDefault();
      cashbackSimulating = false;
      renderState();
    }

    // Idempotent: addEventListener with the same function reference is a
    // no-op on repeat calls. After a cart re-render the buttons are fresh
    // elements that need wiring once.
    function wireCashbackHandlers() {
      var simulate = q('[data-cashback-simulate]');
      if (simulate) simulate.addEventListener('click', startCashbackSimulation);
      var cancel = q('[data-cashback-use-cancel]');
      if (cancel) cancel.addEventListener('click', cancelCashbackSimulation);
    }

    // Renders the free-shipping notice + progress bar.
    // Visibility / copy rules:
    //   - empty cart or no threshold configured → hidden
    //   - has CEP and not in zone → hidden
    //   - has CEP and in zone → "Adicione mais R$ X para obter frete grátis" / "Parabéns…"
    //   - no CEP                → "Faltam R$ X para obter frete grátis em SP e nas capitais…" / "Parabéns…"
    function renderShippingBar() {
      var bar = q('[data-shipping-bar]');
      if (!bar) return;
      var content = bar.querySelector('[data-shipping-bar-content]');
      if (!content) return;

      var rootTotals = q(TOTALS);
      if (!rootTotals) {
        bar.hidden = true;
        return;
      }

      var products = parseInt(rootTotals.getAttribute('data-products-total-cents'), 10) || 0;
      var minCents = parseInt(rootTotals.getAttribute('data-shipping-min-cents'), 10) || 0;
      if (products <= 0 || minCents <= 0) {
        bar.hidden = true;
        return;
      }

      var cep = readCep();
      var inZoneSync = getZoneSync(cep);
      var hasCep = !!cep;

      if (hasCep && inZoneSync === false) {
        bar.hidden = true;
        return;
      }

      if (hasCep && inZoneSync === null) {
        // Async pending: render optimistically as in-zone, then re-evaluate
        // once the city lookup resolves (renderState reruns the full pipeline,
        // not just the bar — eligibility for the calculator depends on the same
        // zone result).
        ensureZoneFetched(cep, function () { renderState(); });
      }

      var balance = getCashbackBalanceCents();
      var simActive = cashbackSimulating && balance > 0;
      var cashbackForBar = simActive ? Math.min(balance, products) : 0;
      var effectiveBase = Math.max(0, products - cashbackForBar);

      // Threshold reached: drop the bar entirely. The shipping row's "Grátis"
      // tag already conveys the win, so the celebratory line is redundant.
      if (effectiveBase >= minCents) {
        bar.hidden = true;
        return;
      }

      bar.hidden = false;

      var remaining = minCents - effectiveBase;
      var pct = Math.max(1, Math.min(100, Math.round(effectiveBase * 100 / minCents)));
      var msg;
      if (hasCep) {
        msg = 'Adicione mais <strong>' + brl(remaining) + '</strong> para obter frete grátis';
      } else {
        msg = 'Faltam <strong>' + brl(remaining) +
              '</strong> para obter frete grátis em SP e nas capitais do Sudeste e Sul.';
      }
      setHtml(content,
        '<div class="not-free-shipping">' +
          '<div class="wrapper-bar"><div class="inner-bar" style="width:' + pct + '%"></div></div>' +
          '<p class="freeBar">' + msg + '</p>' +
        '</div>');
    }

    // The CEP the details line is describing: while editing, whatever is in the
    // input; otherwise the committed one.
    function detailsCep() {
      if (editMode) return isValidCep(editTypedCep) ? editTypedCep : '';
      return readCep();
    }

    function renderDetailsForCurrentCep() {
      var cep = detailsCep();
      if (!cep) {
        renderShippingDetails(null, '');
        return;
      }
      var address = getAddressSync(cep);
      if (address === null) {
        renderShippingDetails(null, '', 'CEP não encontrado');
        return;
      }
      // The prazo belongs to the committed CEP's rate, so it's dropped while the
      // customer is typing a different one.
      renderShippingDetails(address, editMode ? '' : currentEtaText);
    }

    // Central re-render: applies eligibility, redraws control + total + bar + cashback.
    // Called after the rate fetch resolves, after cashback simulation toggles, after
    // the user submits a new CEP, and after async zone resolves.
    function renderState() {
      var rootTotals = q(TOTALS);
      if (!rootTotals) return;
      applyEligibility();
      var controlEl = rootTotals.querySelector('[data-shipping-control]');
      if (controlEl && hasShippingRate && !editMode) {
        renderControlAmount(controlEl);
      }
      updateTotal(rootTotals);
      renderShippingBar();
      renderDetailsForCurrentCep();
    }

    function init() {
      // Also checked here, not just in scheduleInit: a timer queued before the
      // product page paused us would otherwise still fire mid-swap.
      if (window.__vinselCartDrawerShippingPaused) return;
      wireCashbackHandlers();
      var root = q(TOTALS);
      if (!root) {
        // Cart is empty — make sure the bar is hidden.
        renderShippingBar();
        renderShippingDetails(null, '');
        return;
      }
      var rowEl = root.querySelector('[data-shipping-row]');
      var cepSlot = root.querySelector('[data-shipping-cep-slot]');
      var controlEl = root.querySelector('[data-shipping-control]');
      if (!rowEl || !cepSlot || !controlEl) {
        renderShippingBar();
        return;
      }

      var products = parseInt(root.getAttribute('data-products-total-cents'), 10) || 0;
      if (products <= 0) {
        rowEl.hidden = true;
        renderShippingBar();
        renderShippingDetails(null, '');
        return;
      }
      rowEl.hidden = false;

      var cep = readCep();

      // Edit mode (or no CEP at all): input on the left, "calcular" on the right.
      if (editMode || !cep) {
        if (!cepSlot.querySelector('[data-shipping-input]')) {
          renderCepInput(cepSlot, cep || '');
        }
        if (!controlEl.querySelector('.cart-drawer-shipping-submit')) {
          renderControlCalcButton(controlEl);
        }
        // Total: keep cached shipping while edit is pristine (so just clicking
        // the CEP doesn't change the displayed price). Drop it once the user
        // actually modifies the input or when there's no CEP at all.
        if (cep && editPristine) {
          var sigForEdit = cep + '|' + products;
          var cached = rateCache[sigForEdit];
          if (cached && !cached.failed) {
            rawShippingCents = cached.cents;
            hasShippingRate = true;
            currentEtaText = cached.eta || '';
          } else {
            rawShippingCents = 0;
            hasShippingRate = false;
            currentEtaText = '';
          }
        } else {
          rawShippingCents = 0;
          hasShippingRate = false;
          currentEtaText = '';
        }
        applyEligibility();
        updateTotal(root);
        renderShippingBar();
        renderDetailsForCurrentCep();
        return;
      }

      var signature = cep + '|' + products;

      // The address feeds both the confirmation line and the free-shipping zone
      // check, so it's fetched once here and read synchronously from then on.
      var address = getAddressSync(cep);
      if (address === undefined) {
        ensureAddressFetched(cep, function (resolved) {
          if (resolved !== undefined) init();
        });
      }
      if (address === null) {
        // Storage or customer default held a CEP that doesn't exist. Say so
        // rather than sending it to Shopify and reporting "Indisponível".
        var stale = cepSlot.querySelector('.cart-drawer-shipping-cep-link');
        if (!stale || stale.textContent !== formatCep(cep)) renderCepBtn(cepSlot, cep);
        renderControlNotFound(controlEl);
        rawShippingCents = 0;
        hasShippingRate = false;
        currentEtaText = '';
        applyEligibility();
        updateTotal(root);
        renderShippingBar();
        renderDetailsForCurrentCep();
        return;
      }

      // Always paint the CEP button on the left (idempotent — only re-renders
      // when the CEP text actually changes).
      var existing = cepSlot.querySelector('.cart-drawer-shipping-cep-link');
      if (!existing || existing.textContent !== formatCep(cep)) {
        renderCepBtn(cepSlot, cep);
      }

      var cached = rateCache[signature];
      if (cached) {
        if (cached.failed) {
          renderControlError(controlEl);
          rawShippingCents = 0;
          hasShippingRate = false;
          currentEtaText = '';
          applyEligibility();
          updateTotal(root);
          renderShippingBar();
          renderDetailsForCurrentCep();
          return;
        }
        rawShippingCents = cached.cents;
        hasShippingRate = true;
        currentEtaText = cached.eta || '';
        renderState();
        return;
      }

      if (inFlightSignature === signature) {
        if (!controlEl.querySelector('.cart-drawer-shipping-loading')) {
          renderControlLoading(controlEl);
        }
        // Keep the bar in sync with the current cart even while rates are loading.
        applyEligibility();
        updateTotal(root);
        renderShippingBar();
        renderDetailsForCurrentCep();
        return;
      }

      renderControlLoading(controlEl);
      rawShippingCents = 0;
      hasShippingRate = false;
      currentEtaText = '';
      applyEligibility();
      updateTotal(root);
      renderShippingBar();
      renderDetailsForCurrentCep();
      inFlightSignature = signature;

      // The product page empties and refills the cart to quote a single product.
      // A lookup straddling that describes the wrong cart, so its result is
      // dropped rather than shown or cached — init() runs again on resume.
      var swapSeqAtStart = window.__vinselCartSwapSeq || 0;
      function straddledCartSwap() {
        return (
          (window.__vinselCartSwapSeq || 0) !== swapSeqAtStart ||
          window.__vinselCartDrawerShippingPaused === true
        );
      }

      fetchRatesShared(signature, cep).then(function (json) {
        if (inFlightSignature !== signature) return;
        inFlightSignature = '';
        if (straddledCartSwap()) return;

        var fresh = q(TOTALS);
        if (!fresh) return;
        var freshControl = fresh.querySelector('[data-shipping-control]');
        if (!freshControl) return;

        var cheapest = pickCheapest(json && json.shipping_rates);
        if (!cheapest) {
          rateCache[signature] = { failed: true };
          renderControlError(freshControl);
          rawShippingCents = 0;
          hasShippingRate = false;
          currentEtaText = '';
          applyEligibility();
          updateTotal(fresh);
          renderShippingBar();
          renderDetailsForCurrentCep();
          return;
        }
        rateCache[signature] = { cents: cheapest.cents, eta: cheapest.eta };
        rawShippingCents = cheapest.cents;
        hasShippingRate = true;
        currentEtaText = cheapest.eta;
        renderState();
      }).catch(function () {
        if (inFlightSignature !== signature) return;
        inFlightSignature = '';
        if (straddledCartSwap()) return;

        var fresh = q(TOTALS);
        if (!fresh) return;
        var freshControl = fresh.querySelector('[data-shipping-control]');
        rateCache[signature] = { failed: true };
        if (freshControl) renderControlError(freshControl);
        rawShippingCents = 0;
        hasShippingRate = false;
        currentEtaText = '';
        applyEligibility();
        updateTotal(fresh);
        renderShippingBar();
        renderDetailsForCurrentCep();
      });
    }

    var debounceTimer = null;
    function scheduleInit() {
      // product-shipping-calculator.js empties and refills the cart to quote a
      // single product, and each of those writes re-renders this markup.
      // Running here would fire our own prepare_shipping_rates against that
      // temporary cart, and whichever prepare lands last is the one every
      // surface reads back. The product page pauses us for the swap and
      // refreshes us after.
      if (window.__vinselCartDrawerShippingPaused) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(init, 50);
    }

    function start() {
      var root = container();
      if (!root) return false;
      new MutationObserver(scheduleInit).observe(root, {
        childList: true,
        subtree: true
      });
      init();
      return true;
    }

    return { start: start, refresh: scheduleInit };
  }

  function boot() {
    var controllers = [];
    SCOPES.forEach(function (selector) {
      var controller = createController(selector);
      if (controller.start()) controllers.push(controller);
    });

    // Let the product-page calculator re-sync every surface once its cart swap
    // is done and the real cart is back.
    window.__vinselCartDrawerShippingRefresh = function () {
      controllers.forEach(function (c) { c.refresh(); });
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
